import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { useSelector } from 'react-redux';
import ReusableModal from '../Modal/ReusableModal';
import projectService from '../../utils/apiRequests/projectService';
import useThemeStyles from '../../hooks/useThemeStyles';

const ROLE_OPTIONS = [
  { value: 'viewer', label: 'Viewer', description: 'Can read everything' },
  { value: 'commenter', label: 'Commenter', description: 'Can read and add notes' },
  { value: 'editor', label: 'Editor', description: 'Can edit all content' },
];

/**
 * Manage who a project is shared with. Owners invite by email and set
 * view / comment / edit roles; collaborators see the member list and can
 * leave the project.
 */
const ShareProjectModal = ({ open, onClose, projectId, projectTitle, onLeftProject }) => {
  const { colors } = useThemeStyles();
  const currentUserId = useSelector((state) => state.user.userInfo?._id);

  const [loading, setLoading] = useState(false);
  const [owner, setOwner] = useState(null);
  const [collaborators, setCollaborators] = useState([]);
  const [myRole, setMyRole] = useState('owner');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('editor');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null); // { severity, message }

  const isOwner = myRole === 'owner';

  const loadCollaborators = useCallback(async () => {
    setLoading(true);
    setFeedback(null);
    const response = await projectService.getCollaborators(projectId);
    if (!response.error && response.data) {
      setOwner(response.data.owner || null);
      setCollaborators(response.data.collaborators || []);
      setMyRole(response.data.myRole || 'owner');
    } else {
      setFeedback({ severity: 'error', message: 'Could not load the collaborator list.' });
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    if (open && projectId) {
      setEmail('');
      setRole('editor');
      loadCollaborators();
    }
  }, [open, projectId, loadCollaborators]);

  const handleInvite = async (event) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setBusy(true);
    setFeedback(null);
    const response = await projectService.addCollaborator(projectId, trimmed, role);
    if (response.error) {
      const message =
        response.error?.response?.data?.error || 'Could not share the project with that email.';
      setFeedback({ severity: 'error', message });
    } else {
      setCollaborators(response.data?.collaborators || []);
      setEmail('');
      setFeedback({ severity: 'success', message: `Shared with ${trimmed}.` });
    }
    setBusy(false);
  };

  const handleRoleChange = async (collaboratorUserId, nextRole) => {
    setBusy(true);
    const response = await projectService.updateCollaboratorRole(
      projectId,
      collaboratorUserId,
      nextRole
    );
    if (response.error) {
      setFeedback({ severity: 'error', message: 'Could not update the role.' });
    } else {
      setCollaborators(response.data?.collaborators || []);
    }
    setBusy(false);
  };

  const handleRemove = async (collaboratorUserId) => {
    setBusy(true);
    const isSelf = String(collaboratorUserId) === String(currentUserId);
    const response = await projectService.removeCollaborator(projectId, collaboratorUserId);
    if (response.error) {
      setFeedback({ severity: 'error', message: 'Could not remove the collaborator.' });
      setBusy(false);
      return;
    }
    if (isSelf) {
      onClose();
      if (onLeftProject) onLeftProject();
    } else {
      setCollaborators(response.data?.collaborators || []);
    }
    setBusy(false);
  };

  const selectSx = {
    fontSize: '0.8125rem',
    minWidth: 120,
    '& .MuiSelect-select': { py: 0.5 },
  };

  return (
    <ReusableModal open={open} onClose={onClose} title={`Share “${projectTitle}”`}>
      <Box sx={{ minWidth: { sm: 440 } }}>
        {isOwner && (
          <Box component="form" onSubmit={handleInvite} sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              size="small"
              fullWidth
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="Invite by email…"
              inputProps={{ 'aria-label': 'Collaborator email' }}
            />
            <Select
              size="small"
              value={role}
              onChange={(event) => setRole(event.target.value)}
              sx={selectSx}
              inputProps={{ 'aria-label': 'Role for new collaborator' }}
            >
              {ROLE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <Button type="submit" variant="contained" disabled={busy || !email.trim()}>
              Share
            </Button>
          </Box>
        )}

        {feedback && (
          <Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
            {feedback.message}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Owner row */}
            {owner && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: '0.875rem' }}>{owner.name}</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.75rem', color: colors.text.muted }}>
                  Owner
                </Typography>
              </Box>
            )}

            {collaborators.map((collaborator) => {
              const collaboratorId = String(collaborator.userId);
              const isSelf = collaboratorId === String(currentUserId);
              return (
                <Box
                  key={collaboratorId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 0.75,
                    borderTop: `1px solid ${colors.border.subtle}`,
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography noWrap sx={{ fontSize: '0.875rem' }}>
                      {collaborator.name}
                      {isSelf ? ' (you)' : ''}
                    </Typography>
                    <Typography noWrap sx={{ fontSize: '0.75rem', color: colors.text.muted }}>
                      {collaborator.email}
                    </Typography>
                  </Box>
                  {isOwner ? (
                    <Select
                      size="small"
                      value={collaborator.role}
                      onChange={(event) => handleRoleChange(collaboratorId, event.target.value)}
                      disabled={busy}
                      sx={selectSx}
                      inputProps={{ 'aria-label': `Role for ${collaborator.name}` }}
                    >
                      {ROLE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <Typography
                      sx={{ fontSize: '0.75rem', color: colors.text.muted, minWidth: 80 }}
                    >
                      {ROLE_OPTIONS.find((o) => o.value === collaborator.role)?.label ||
                        collaborator.role}
                    </Typography>
                  )}
                  {(isOwner || isSelf) && (
                    <Tooltip title={isSelf ? 'Leave project' : 'Remove'}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={busy}
                          onClick={() => handleRemove(collaboratorId)}
                          aria-label={isSelf ? 'Leave project' : `Remove ${collaborator.name}`}
                        >
                          <PersonRemoveIcon sx={{ fontSize: '1.05rem' }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              );
            })}

            {collaborators.length === 0 && (
              <Typography sx={{ fontSize: '0.8125rem', color: colors.text.muted, py: 1 }}>
                {isOwner
                  ? 'Not shared with anyone yet. Invite someone by email above.'
                  : 'No other collaborators.'}
              </Typography>
            )}
          </Box>
        )}

        {/* Role legend */}
        <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${colors.border.subtle}` }}>
          {ROLE_OPTIONS.map((option) => (
            <Typography
              key={option.value}
              sx={{ fontSize: '0.6875rem', color: colors.text.muted, lineHeight: 1.7 }}
            >
              <strong>{option.label}</strong> — {option.description}
            </Typography>
          ))}
        </Box>
      </Box>
    </ReusableModal>
  );
};

ShareProjectModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.string,
  projectTitle: PropTypes.string,
  onLeftProject: PropTypes.func,
};

ShareProjectModal.defaultProps = {
  projectId: '',
  projectTitle: '',
  onLeftProject: undefined,
};

export default ShareProjectModal;
